/**
 * PROTECTED GATE LOGIC - This is intentional and documented.
 * Any changes require approval per Product UX Playbook.
 * 
 * Rules:
 * 1. First product page = ungated
 * 2. Gate triggers on 2nd product interaction only
 * 3. One gate per session maximum
 * 4. Dismissible with session memory
 * 5. No gate on: Homepage, first product page, ad landings
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useEditMode } from '@/contexts/EditModeContext';
import { useProduct } from '@/hooks/useProducts';
import { useProductEditor } from '@/hooks/useProductEditor';
import { useProductQA } from '@/hooks/useProductQA';
import { useProductPlans } from '@/hooks/useProductPlans';
import { useBookingModal } from '@/hooks/useBookingModal';
import { useProductViewTracking } from '@/hooks/useBrowsingActivity';
import { useProductPromoCodes } from '@/hooks/usePromoCodes';
import { mockProducts } from '@/lib/mockData';
import { requireUser } from '@/lib/requireUser';
import { trackGateEvent } from '@/data/gateAnalytics';
import { isFeatureEnabled } from '@/lib/featureFlags';
import { useIsMobile } from '@/hooks/use-mobile';

// Session storage keys for gate logic
const GATE_PAGES_VIEWED_KEY = 'cn_product_pages_viewed';
const GATE_SHOWN_KEY = 'cn_gate_shown_this_session';

export function useProductDetailState() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isEditor, membership } = useAuth();
  const { isEditMode } = useEditMode();
  const isMobile = useIsMobile();
  
  // Data fetching
  const { data: dbProduct, isLoading } = useProduct(slug || '');
  const product = dbProduct || mockProducts.find(p => p.id === slug);
  
  // Track product view
  useProductViewTracking(product?.id);
  
  // Product editing
  const { updateProduct, deleteProduct, isDeleting } = useProductEditor();
  const { updateQA, generateQA, isGenerating } = useProductQA();
  const { updatePlans } = useProductPlans();
  const { openBooking, BookingModal, PreloadIframe } = useBookingModal(
    product?.title || '', 
    product?.booking_url,
    product ? {
      id: product.id,
      name: product.title,
      vendorEmail: (product as any).vendor_email || undefined,
      vendorPhone: (product as any).vendor_phone || undefined,
    } : undefined
  );
  
  // Promo codes
  const { data: promoCodes } = useProductPromoCodes(product?.id || '');
  const hasPromo = isFeatureEnabled('promoCodes') && promoCodes && promoCodes.length > 0;
  
  // UI State
  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(product?.image || '');
  const [demoModalOpen, setDemoModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [tacoFilter, setTacoFilter] = useState('all');
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState('reviews');
  const [enrichModalOpen, setEnrichModalOpen] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [headerVisible, setHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [showGate, setShowGate] = useState(false);
  const [gateShownAt, setGateShownAt] = useState<number | null>(null);
  
  // Product type checks
  const isService = !!product?.service_type;
  const isCourse = product?.service_type === 'education';
  const hasAffiliatePlans = product?.plans?.some(plan => plan.features?.affiliate_url);
  
  // Scroll handling for header visibility
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setHeaderVisible(currentScrollY < 50);
      setLastScrollY(currentScrollY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);
  
  // Close gate handler - marks session as gate-shown
  const handleCloseGate = useCallback(() => {
    sessionStorage.setItem(GATE_SHOWN_KEY, 'true');
    setShowGate(false);
  }, []);

  // Product gate overlay logic - value-first, intent-based gate
  // PROTECTED LOGIC: First product page = ungated, gate only on 2nd+ page visit
  useEffect(() => {
    // Never gate authenticated users
    if (user) return;
    
    // Check if gate was already shown this session (dismissed or signed up)
    const gateAlreadyShown = sessionStorage.getItem(GATE_SHOWN_KEY) === 'true';
    if (gateAlreadyShown) return;
    
    // Get current page view count
    const currentCount = parseInt(sessionStorage.getItem(GATE_PAGES_VIEWED_KEY) || '0', 10);
    
    // Increment count for this product visit
    sessionStorage.setItem(GATE_PAGES_VIEWED_KEY, String(currentCount + 1));
    
    // First product page = ungated (count was 0 before increment)
    if (currentCount < 1) return;
    
    // This is the 2nd+ product page - apply timing rules
    let timer: number | undefined;
    let triggered = false;
    
    const triggerGate = (trigger: 'timer' | 'scroll') => {
      if (triggered) return;
      triggered = true;
      const now = Date.now();
      setGateShownAt(now);
      trackGateEvent('impression', slug, trigger);
      setShowGate(true);
    };
    
    // Mobile: 50% scroll only (no timer)
    // Desktop: 7 seconds OR 30% scroll (whichever first)
    if (!isMobile) {
      timer = window.setTimeout(() => {
        triggerGate('timer');
      }, 7000);
    }
    
    const scrollThreshold = isMobile ? 50 : 30; // 50% mobile, 30% desktop
    
    const onScroll = () => {
      const scrollPercent = (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100;
      if (scrollPercent > scrollThreshold && !triggered) {
        triggerGate('scroll');
      }
    };
    
    window.addEventListener('scroll', onScroll);
    return () => {
      if (timer) window.clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
    };
  }, [user, slug, isMobile]);
  
  // Sync selectedImage with product.image
  useEffect(() => {
    if (product?.image) {
      setSelectedImage(product.image);
    }
  }, [product?.image]);
  
  // Handlers
  const handleUpdateField = async (field: string, value: any) => {
    if (slug) {
      await updateProduct({ productId: slug, field, value });
    }
  };
  
  const handleUpdateQA = async (qaItems: { question: string; answer: string }[]) => {
    if (slug) {
      await updateQA({ productId: slug, qaItems });
    }
  };
  
  const handleSavePlans = async (plans: Array<{ name: string; price: number; features: string[] }>) => {
    if (slug) {
      await updatePlans({ productId: slug, plans });
    }
  };
  
  const handleBuyNow = () => {
    if (hasAffiliatePlans) {
      document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (isCourse && product?.access_url) {
      window.open(product.access_url, '_blank');
      return;
    }
    if (isService && product?.booking_url) {
      openBooking('Discovery Call');
    } else {
      requireUser({
        user,
        navigate,
        currentPath: location.pathname,
        action: () => setPurchaseModalOpen(true)
      });
    }
  };
  
  const handleSelectPlan = (planIndex: number) => {
    if (hasAffiliatePlans) return;
    if (isService && product?.booking_url && product?.plans[planIndex]) {
      openBooking(product.plans[planIndex].name);
    } else {
      handleBuyNow();
    }
  };
  
  const handleDelete = () => {
    deleteProduct(slug!);
    setShowDeleteDialog(false);
    navigate('/browse');
  };
  
  return {
    // Route params
    slug,
    
    // Auth & editing
    user,
    isEditor,
    membership,
    isEditMode,
    
    // Data
    product,
    isLoading,
    promoCodes,
    hasPromo,
    
    // Product types
    isService,
    isCourse,
    hasAffiliatePlans,
    
    // UI State
    purchaseModalOpen,
    setPurchaseModalOpen,
    selectedImage,
    setSelectedImage,
    demoModalOpen,
    setDemoModalOpen,
    searchQuery,
    setSearchQuery,
    sortBy,
    setSortBy,
    tacoFilter,
    setTacoFilter,
    isDescriptionExpanded,
    setIsDescriptionExpanded,
    activeTab,
    setActiveTab,
    enrichModalOpen,
    setEnrichModalOpen,
    showDeleteDialog,
    setShowDeleteDialog,
    headerVisible,
    showGate,
    setShowGate,
    gateShownAt,
    handleCloseGate,
    
    // Handlers
    handleUpdateField,
    handleUpdateQA,
    handleSavePlans,
    handleBuyNow,
    handleSelectPlan,
    handleDelete,
    
    // Mutations
    isDeleting,
    isGenerating,
    generateQA,
    
    // Booking
    openBooking,
    BookingModal,
    PreloadIframe,
    
    // Navigation
    navigate,
  };
}
